import React from 'react';
import { Box, Typography, Tooltip } from 'omaha-design-system';
import type { PlayerRotation } from './types';
import { formatPlayerName, ROTATION_COLORS } from './constants';
import StintBar from './StintBar';

// Hoisted to module scope for stable Emotion references (React.memo optimization)
const rowBase = {
  display: 'flex',
  alignItems: 'center',
  height: 40,
  borderBottom: `1px solid ${ROTATION_COLORS.border.faint}`,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(30, 35, 48, 0.4)',
  },
} as const;

const rowOdd = {
  ...rowBase,
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
} as const;

const rowSelected = {
  ...rowBase,
  backgroundColor: 'rgba(42, 63, 95, 0.6)',
  '&:hover': {
    backgroundColor: 'rgba(42, 63, 95, 0.7)',
  },
} as const;

const playerNameBase = {
  width: 140,
  pl: 1,
  pr: 1,
  fontSize: '0.9rem',
  color: ROTATION_COLORS.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'color 0.15s',
  '&:hover': {
    color: ROTATION_COLORS.accent.lightBlue,
  },
} as const;

const playerNameSelected = {
  ...playerNameBase,
  color: ROTATION_COLORS.accent.blue,
} as const;

const statCellBase = {
  textAlign: 'center',
  fontSize: '0.9rem',
  fontFamily: 'monospace',
} as const;

const stintsContainerSx = {
  flex: 1,
  position: 'relative',
  height: 32,
  ml: 0.5,
  mr: 0.5,
  overflow: 'hidden',
} as const;

const periodDividerSx = {
  position: 'absolute',
  top: 0,
  height: '100%',
  width: '1px',
  backgroundColor: ROTATION_COLORS.border.subtle,
} as const;

const plusMinusCellSx = {
  width: 48,
  textAlign: 'right',
  pr: 1,
  fontSize: '0.9rem',
  fontFamily: 'monospace',
  fontWeight: 600,
} as const;

interface PlayerRowProps {
  player: PlayerRotation;
  gameDuration: number;
  allPlayers: PlayerRotation[];
  opponentPlayers: PlayerRotation[];
  isSelected: boolean;
  onSelect: (player: PlayerRotation, e: React.MouseEvent) => void;
  teamAbbrev: string;
  opponentAbbrev: string;
  index: number;
}

const PlayerRow: React.FC<PlayerRowProps> = ({
  player,
  gameDuration,
  allPlayers,
  opponentPlayers,
  isSelected,
  onSelect,
  teamAbbrev,
  opponentAbbrev,
  index,
}) => {
  const displayName = formatPlayerName(player.playerName);
  const stealBlocks = (player.stats.stl ?? 0) + (player.stats.blk ?? 0);
  const isOdd = index % 2 === 1;

  const rowSx = isSelected ? rowSelected : isOdd ? rowOdd : rowBase;
  const nameSx = isSelected ? playerNameSelected : playerNameBase;

  const pmColor = player.totalPlusMinus > 0
    ? ROTATION_COLORS.pm.positive
    : player.totalPlusMinus < 0
    ? ROTATION_COLORS.pm.negative
    : ROTATION_COLORS.text.muted;

  return (
    <Box sx={rowSx}>
      <Tooltip title={isSelected ? 'Click to remove from comparison' : 'Click to add to comparison'} placement="top" arrow>
        <Typography
          component="div"
          sx={nameSx}
          title={player.playerName}
          onClick={(e: React.MouseEvent) => onSelect(player, e)}
        >
          {displayName}{isSelected ? ' \u2713' : ''}
        </Typography>
      </Tooltip>

      <Typography sx={{ ...statCellBase, width: 40, color: ROTATION_COLORS.text.secondary }}>{Math.round(player.totalMinutes)}</Typography>
      <Typography sx={{ ...statCellBase, width: 40, color: ROTATION_COLORS.text.primary, fontWeight: 500 }}>{player.stats.pts}</Typography>
      <Typography sx={{ ...statCellBase, width: 36, color: ROTATION_COLORS.text.secondary }}>{player.stats.reb}</Typography>
      <Typography sx={{ ...statCellBase, width: 36, color: ROTATION_COLORS.text.secondary }}>{player.stats.ast}</Typography>
      <Typography sx={{ ...statCellBase, width: 36, color: ROTATION_COLORS.text.secondary }}>{stealBlocks}</Typography>
      <Typography sx={{ ...statCellBase, width: 36, color: ROTATION_COLORS.text.secondary }}>{player.stats.pf ?? 0}</Typography>

      <Box sx={stintsContainerSx}>
        {player.stints.map((stint, idx) => (
          <StintBar
            key={idx}
            stint={stint}
            gameDuration={gameDuration}
            playerName={player.playerName}
            allPlayers={allPlayers}
            opponentPlayers={opponentPlayers}
            teamAbbrev={teamAbbrev}
            opponentAbbrev={opponentAbbrev}
          />
        ))}

        {[720, 1440, 2160]
          .filter((t) => t < gameDuration)
          .map((time) => (
            <Box
              key={time}
              sx={periodDividerSx}
              style={{ left: `${(time / gameDuration) * 100}%` }}
            />
          ))}
      </Box>

      <Typography sx={plusMinusCellSx} style={{ color: pmColor }}>
        {player.totalPlusMinus > 0 ? '+' : ''}
        {player.totalPlusMinus}
      </Typography>
    </Box>
  );
};

export default React.memo(PlayerRow);
